import { create } from 'zustand';

import {
  beginGuideAnchorWatch,
  stopGuideAnchorWatch,
  type AnchorWatchHost,
  type GuideAnchorStatus,
} from './anchorWatch';
import { GUIDE_NOTICE, guideParamsFromContext } from './copy';
import { canStartGuideOnThisScreen } from './layoutGate';
import { postGuideChatLine, type GuideChatFields } from './messages';
import { guideNavigate } from './navigate';
import type { GuideContext, GuideDefinition, GuideId, GuideRun, GuideStep } from './types';

/** Dispatched by a wizard when its dialog closes, and accepted by `emitGuideEvent`. */
export const GUIDE_DIALOG_CLOSED_EVENT = 'guide.dialogClosed';

interface GuideState {
  active: GuideRun | null;
  anchorEl: HTMLElement | null;
  anchorStatus: GuideAnchorStatus;
  start: (definition: GuideDefinition) => boolean;
  next: () => void;
  back: () => void;
  skip: () => void;
  cancel: () => void;
  finish: () => void;
}

function findStep(definition: GuideDefinition, stepId: string): GuideStep | undefined {
  return definition.steps.find((step) => step.id === stepId);
}

function resolveNextId(step: GuideStep, ctx: GuideContext): string | null {
  if (step.next === undefined) return null;
  if (typeof step.next === 'function') return step.next(ctx);
  return step.next;
}

function withParams(fields: GuideChatFields, ctx: GuideContext): GuideChatFields {
  const guideParams = guideParamsFromContext(ctx);
  if (!guideParams) return fields;
  return { ...fields, guideParams };
}

export function guideProgress(active: GuideRun): { current: number; total: number } {
  const total = active.guide.steps.length;
  const index = active.guide.steps.findIndex((step) => step.id === active.stepId);
  return { current: index < 0 ? 1 : index + 1, total };
}

export const useGuideStore = create<GuideState>()((set, get) => {
  const host: AnchorWatchHost = {
    isCurrent: (stepId) => get().active?.stepId === stepId,
    getAnchor: () => get().anchorEl,
    getStatus: () => get().anchorStatus,
    setAnchor: (element, status) => set({ anchorEl: element, anchorStatus: status }),
  };

  function enterCurrent(): void {
    const active = get().active;
    if (!active) return;
    const step = findStep(active.guide, active.stepId);
    if (!step) {
      get().cancel();
      return;
    }
    postGuideChatLine(withParams({ guideTextKey: step.textKey }, active.ctx));
    let focus = false;
    for (const action of step.onEnter ?? []) {
      if (action.type === 'navigate') guideNavigate(action.to);
      if (action.type === 'focus') focus = true;
    }
    beginGuideAnchorWatch(step.anchor, step.id, focus, host);
  }

  function advance(): void {
    const active = get().active;
    if (!active) return;
    const step = findStep(active.guide, active.stepId);
    if (!step) {
      get().cancel();
      return;
    }
    const nextId = resolveNextId(step, active.ctx);
    if (nextId === null) {
      get().finish();
      return;
    }
    if (!findStep(active.guide, nextId)) {
      get().cancel();
      return;
    }
    stopGuideAnchorWatch();
    set({
      active: {
        ...active,
        stepId: nextId,
        history: [...active.history, active.stepId],
      },
      anchorEl: null,
      anchorStatus: 'pending',
    });
    enterCurrent();
  }

  return {
    active: null,
    anchorEl: null,
    anchorStatus: 'idle',

    start: (definition) => {
      if (!canStartGuideOnThisScreen()) {
        postGuideChatLine({ guideNoticeKey: GUIDE_NOTICE.narrowScreen });
        return false;
      }
      if (!findStep(definition, definition.firstStep)) {
        postGuideChatLine({ guideNoticeKey: GUIDE_NOTICE.stopped });
        return false;
      }
      stopGuideAnchorWatch();
      set({
        active: {
          guide: definition,
          stepId: definition.firstStep,
          ctx: {},
          history: [],
        },
        anchorEl: null,
        anchorStatus: 'pending',
      });
      enterCurrent();
      return true;
    },

    next: () => {
      advance();
    },

    skip: () => {
      advance();
    },

    back: () => {
      const active = get().active;
      if (!active || active.history.length === 0) return;
      const previous = active.history[active.history.length - 1];
      if (previous === undefined) return;
      stopGuideAnchorWatch();
      set({
        active: {
          ...active,
          stepId: previous,
          history: active.history.slice(0, -1),
        },
        anchorEl: null,
        anchorStatus: 'pending',
      });
      enterCurrent();
    },

    cancel: () => {
      const active = get().active;
      stopGuideAnchorWatch();
      if (!active) {
        set({ anchorEl: null, anchorStatus: 'idle' });
        return;
      }
      set({ active: null, anchorEl: null, anchorStatus: 'idle' });
      postGuideChatLine({ guideNoticeKey: GUIDE_NOTICE.stopped });
    },

    finish: () => {
      const active = get().active;
      if (!active) return;
      const doneTextKey = active.guide.doneTextKey;
      const ctx = active.ctx;
      stopGuideAnchorWatch();
      set({ active: null, anchorEl: null, anchorStatus: 'idle' });
      postGuideChatLine(
        withParams({ guideTextKey: doneTextKey, guideNoticeKey: GUIDE_NOTICE.finished }, ctx),
      );
    },
  };
});

export function useGuideActive(): boolean {
  return useGuideStore((state) => state.active !== null);
}

export function isGuideActive(): boolean {
  return useGuideStore.getState().active !== null;
}

export function getGuidePageContext(): { guideId: GuideId; stepId: string } | undefined {
  const active = useGuideStore.getState().active;
  if (!active) return undefined;
  return { guideId: active.guide.id, stepId: active.stepId };
}

/**
 * Report a step event. No-op when no guide is running. Merges `payload` into
 * the guide context and advances when it matches the active step's `doneWhen`.
 * `guide.dialogClosed` ends the guide instead of completing a step.
 */
export function emitGuideEvent(name: string, payload?: Record<string, unknown>): void {
  const active = useGuideStore.getState().active;
  if (!active) return;
  if (name === GUIDE_DIALOG_CLOSED_EVENT) {
    useGuideStore.getState().cancel();
    return;
  }
  const step = findStep(active.guide, active.stepId);
  if (payload) {
    const current = useGuideStore.getState().active;
    if (!current) return;
    useGuideStore.setState({
      active: { ...current, ctx: { ...current.ctx, ...payload } },
    });
  }
  if (step?.doneWhen === name) {
    useGuideStore.getState().next();
  }
}

function onGuideDialogClosed(): void {
  useGuideStore.getState().cancel();
}

if (typeof window !== 'undefined') {
  window.addEventListener(GUIDE_DIALOG_CLOSED_EVENT, onGuideDialogClosed);
}
